import {
  AccountInfo,
  Configuration,
  PublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import { useLogger } from "./logger";

const MSAL_TIMEOUT_MS = 15000; // 15 second timeout for MSAL operations

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${operation} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

const redirectUri = new URL("/auth/callback", location.href).toString();

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri,
    navigateToLoginRequestUrl: false,
    postLogoutRedirectUri: window.location.origin + "/welcome",
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

const msalInstance =
  await PublicClientApplication.createPublicClientApplication(msalConfig);

const scopes = ["openid", "profile", "email"];

const logger = useLogger();

export const AuthenticationService = {
  requiresTokenRefresh(): boolean {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return false;
    }

    const now: number = Math.floor(new Date().getTime() / 1000);
    const expiry: number = account.idTokenClaims?.exp || 0;
    return now - 300 > expiry; // 5 minutes buffer
  },

  getSavedUser(): AccountInfo | null {
    // Just return cached account - token refresh is handled by api-configuration.ts
    // when actually needed. Removing eager refresh eliminates race condition with
    // handleRedirectPromise() on callback URL.
    return msalInstance.getActiveAccount();
  },

  async loginRedirect(): Promise<void> {
    logger.trace('Authentication: loginRedirect()');
    await msalInstance.clearCache();
    await msalInstance.loginRedirect({ scopes, prompt: 'select_account' });
  },

  async isAuthenticated(): Promise<boolean> {
    try {
      const res = await withTimeout(
        msalInstance.handleRedirectPromise(),
        MSAL_TIMEOUT_MS,
        'handleRedirectPromise'
      );
      if (res) {
        msalInstance.setActiveAccount(res.account);
      }
    } catch (error) {
      console.error('isAuthenticated error:', error);
      // On timeout, check if we have cached accounts
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      return true;
    }
    return false;
  },

  getActiveAccount(): AccountInfo | null {
    return msalInstance.getActiveAccount();
  },

  async clearCache(): Promise<void> {
    const account = msalInstance.getActiveAccount();
    await msalInstance.clearCache({ account });
  },

  async handleTokenRefresh(): Promise<AccountInfo> {
    logger.trace('Authentication: handleTokenRefresh()');
    const currentAccount = msalInstance.getActiveAccount();
    const { account } = await withTimeout(
      msalInstance.acquireTokenSilent({
        forceRefresh: true,
        account: currentAccount!,
        scopes,
      }),
      MSAL_TIMEOUT_MS,
      'acquireTokenSilent'
    );

    return account;
  },
};


export function useAuthentication() {
  return AuthenticationService;
}